// Google Analytics integration utilities

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export class GoogleAnalytics {
  private static trackingId = 'G-FE4S56XNVT';
  private static isEnabled = true;

  // Initialize Google Analytics
  static init() {
    if (!this.isEnabled || typeof window === 'undefined') return;

    // Check if gtag is already loaded
    if (window.gtag) {
      console.log('Google Analytics already initialized');
      return;
    }

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Configure gtag
    window.gtag('js', new Date());
    window.gtag('config', this.trackingId);

    console.log('Google Analytics initialized with ID:', this.trackingId);
  }

  // Track page views
  static trackPageView(path: string, title?: string) {
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('config', this.trackingId, {
      page_path: path,
      page_title: title || document.title
    });

    console.log('GA: Page view tracked:', path);
  }

  // Track custom events
  static trackEvent(action: string, category: string, label?: string, value?: number) {
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    });

    console.log('GA: Event tracked:', { action, category, label, value });
  }

  // Track e-commerce events
  static trackPurchase(transactionId: string, value: number, currency: string = 'USD', items: any[] = []) {
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items
    });

    console.log('GA: Purchase tracked:', { transactionId, value, currency, items });
  }

  // Track user interactions
  static trackUserInteraction(action: string, element: string) {
    this.trackEvent(action, 'User Interaction', element);
  }

  // Track business events
  static trackBusinessEvent(action: string, details?: any) {
    this.trackEvent(action, 'Business', JSON.stringify(details));
  }

  // Enable/disable tracking
  static setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    
    if (enabled) {
      this.init();
    } else {
      // Disable GA tracking
      if (window.gtag) {
        window.gtag('config', this.trackingId, {
          send_page_view: false
        });
      }
    }

    console.log('Google Analytics', enabled ? 'enabled' : 'disabled');
  }

  // Check if analytics is enabled
  static isAnalyticsEnabled(): boolean {
    return this.isEnabled;
  }

  // Get tracking ID
  static getTrackingId(): string {
    return this.trackingId;
  }

  // Update tracking ID (for admin configuration)
  static updateTrackingId(newTrackingId: string) {
    this.trackingId = newTrackingId;
    
    if (this.isEnabled) {
      // Reinitialize with new tracking ID
      this.init();
    }
  }
}

// Convenience functions for common tracking scenarios
const trackPageView = (path: string, title?: string) => {
  GoogleAnalytics.trackPageView(path, title);
};

const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  GoogleAnalytics.trackEvent(action, category, label, value);
};

const trackUserAction = (action: string, element: string) => {
  GoogleAnalytics.trackUserInteraction(action, element);
};

const trackBusinessAction = (action: string, details?: any) => {
  GoogleAnalytics.trackBusinessEvent(action, details);
};

// Initialize analytics on module load
if (typeof window !== 'undefined') {
  GoogleAnalytics.init();
}